import "@genius-tracker/design-system/tokens.css";
import "@genius-tracker/design-system/reset.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@genius-tracker/design-system";
import { LocaleProvider, loadAdminCatalog } from "@genius-tracker/i18n/react";
import type { Locale } from "@genius-tracker/i18n";
import { AdminApp } from "./AdminApp.js";
import { AdminRootBoundary } from "./AdminRootBoundary.js";
import { SessionProvider } from "./session/SessionProvider.js";
import {
  buildUnauthenticatedAwareMutationCache,
  buildUnauthenticatedAwareQueryCache,
} from "./lib/unauthenticatedHandler.js";
import { retryDelayForQuery, shouldRetryQuery } from "./lib/rateLimitRetry.js";

const defaultLocale = (import.meta.env.VITE_DEFAULT_LOCALE as Locale | undefined) ?? "en";

/**
 * GT-FI-MMP-COMPLETION-01 Wave 1 — wire the global mid-session 401
 * redirect through the QueryClient's caches. See
 * `apps/admin/src/lib/unauthenticatedHandler.ts`.
 */
const queryClient = new QueryClient({
  queryCache: buildUnauthenticatedAwareQueryCache(),
  mutationCache: buildUnauthenticatedAwareMutationCache(),
  defaultOptions: {
    queries: {
      // GT-…-RATE-LIMIT-FIX-02 — 429-aware retry policy (see
      // `lib/rateLimitRetry`): never retry a 429 before its
      // `Retry-After`; one backoff retry for transient errors only.
      retry: shouldRetryQuery,
      retryDelay: retryDelayForQuery,
      // Was `true`. Phase-0 traced an Admin Ingest 429 storm to a
      // focus-refetch burst (the panel already overrode this to
      // `false`); flipping the global default to `false` removes the
      // same risk on every other admin query and stops the admin tab
      // from contributing focus-refetch bursts to the per-IP budget it
      // shares with the customer web app. Individual queries can opt
      // back in where live freshness is genuinely needed.
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root element");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider initialTheme="light">
        {/*
          E-I18N-SPLIT1 — die Kataloge sind je Sprache in eine Kunden- und eine
          Admin-Haelfte geteilt. Die Admin-App ist die einzige, die die
          `admin.*` / `admin_hub.*` Schluessel braucht, und holt sie deshalb
          ueber `loadAdminCatalog` als zweite Datei dazu. Fuer die Kunden-App
          faellt diese Haelfte damit ganz aus dem Bundle.
        */}
        <LocaleProvider initialLocale={defaultLocale} extraCatalogLoader={loadAdminCatalog}>
          {/*
            A-GTCC-ROOT-BOUNDARY1 — below the providers its fallback card needs
            (theme, locale), above everything it guards. Without it a render
            throw outside the GTCC view boundary blanked the whole app.
          */}
          <AdminRootBoundary>
            <SessionProvider>
              <AdminApp />
            </SessionProvider>
          </AdminRootBoundary>
        </LocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
